import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Wifi, Search, Download, CheckCircle2, History, ChevronDown, ChevronUp, Upload, ArrowDownToLine } from 'lucide-react'
import { NetBoxConnectionStep } from './netbox/NetBoxConnectionStep'
import { NetBoxPreviewStep } from './netbox/NetBoxPreviewStep'
import { NetBoxApplyStep } from './netbox/NetBoxApplyStep'
import { NetBoxSchedulePanel } from './netbox/NetBoxSchedulePanel'
import { NetBoxRoleMappingEditor } from './netbox/NetBoxRoleMappingEditor'
import { NetBoxHistoryPanel } from './netbox/NetBoxHistoryPanel'
import NetBoxExportWizard from './NetBoxExportWizard'

interface Site {
  id: number; name: string; slug: string
  status: string; racks_count: number; devices_count: number
}

type WizardStep = 0 | 1 | 2
type ActiveTab = 'import' | 'export'

const STEPS = [
  { icon: Wifi,         label: 'Подключение' },
  { icon: Search,       label: 'Предпросмотр' },
  { icon: Download,     label: 'Импорт'       },
]

export default function NetBoxPage() {
  const params = useParams<{ dcCode?: string }>()
  const [activeTab, setActiveTab] = useState<ActiveTab>('import')
  const [step, setStep] = useState<WizardStep>(0)
  const [showHistory, setShowHistory] = useState(false)

  // Данные из шага 1
  const [connUrl, setConnUrl]     = useState('')
  const [connToken, setConnToken] = useState('')
  const [sites, setSites]         = useState<Site[]>([])

  // Данные из шага 2
  const [previewResult, setPreviewResult]     = useState<any>(null)
  const [selectedDc, setSelectedDc]           = useState(params.dcCode || '')
  const [selectedSiteId, setSelectedSiteId]   = useState<number | null>(null)
  const [conflictStrategy, setConflictStrategy] = useState('netbox_wins')
  const [selectedRackNbIds, setSelectedRackNbIds] = useState<number[] | null>(null)

  const [hasSavedConnection, setHasSavedConnection] = useState(false)

  function handleConnected(url: string, token: string, siteList: Site[], defaultSiteId?: number | null) {
    setConnUrl(url); setConnToken(token); setSites(siteList)
    if (defaultSiteId != null) setSelectedSiteId(defaultSiteId)
    setHasSavedConnection(true)
    setStep(1)
  }

  function handlePreviewReady(
    preview: any, dcCode: string, siteId: number | null,
    strategy: string, rackIds: number[] | null
  ) {
    setPreviewResult(preview)
    setSelectedDc(dcCode)
    setSelectedSiteId(siteId)
    setConflictStrategy(strategy)
    setSelectedRackNbIds(rackIds)
    setStep(2)
  }

  return (
    <div className="flex flex-col h-full bg-bg-base">
      {/* Tabs */}
      <div className="border-b border-border px-6 pt-4 flex items-center gap-0">
        {([
          { id: 'import', label: 'NetBox → App', icon: ArrowDownToLine, desc: 'Импорт' },
          { id: 'export', label: 'App → NetBox', icon: Upload, desc: 'Экспорт' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 border-b-2 text-sm font-medium transition-all -mb-px ${
              activeTab === tab.id
                ? 'border-accent-primary text-accent-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Export tab */}
      {activeTab === 'export' && <NetBoxExportWizard />}

      {/* Import tab */}
      {activeTab === 'import' && (
        <>
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Импорт из NetBox</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Синхронизация стоек, устройств, портов и кабелей из NetBox в DC Cable Link
          </p>
        </div>
        <button
          onClick={() => setShowHistory(h => !h)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${
            showHistory
              ? 'bg-accent-primary/10 text-accent-primary border-accent-primary/30'
              : 'border-border text-text-secondary hover:text-text-primary hover:border-accent-primary/30'
          }`}
        >
          <History size={14} />
          История
          {showHistory ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
        </button>
      </div>

      {/* History panel (collapsible) */}
      {showHistory && (
        <div className="border-b border-border px-6 py-4 bg-bg-panel max-h-[420px] overflow-y-auto">
          <NetBoxHistoryPanel dcCode={selectedDc} />
        </div>
      )}

      {/* Step indicator */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => {
            const Icon = s.icon
            const done = i < step
            const active = i === step
            return (
              <div key={i} className="flex items-center">
                <button
                  onClick={() => i < step && setStep(i as WizardStep)}
                  disabled={i > step}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    active  ? 'bg-accent-primary/10 text-accent-primary' :
                    done    ? 'text-green-400 hover:bg-green-400/10 cursor-pointer' :
                              'text-text-muted cursor-default'
                  }`}
                >
                  {done ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  {s.label}
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 h-px mx-1 ${i < step ? 'bg-green-500/50' : 'bg-border'}`} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {step === 0 && (
            <div className="space-y-4">
              <NetBoxConnectionStep
                dcCode={selectedDc}
                onConnected={handleConnected}
                onSavedConnection={() => setHasSavedConnection(true)}
              />
              <NetBoxSchedulePanel
                dcCode={selectedDc}
                hasSavedConnection={hasSavedConnection}
                defaultSiteId={selectedSiteId}
              />
              <NetBoxRoleMappingEditor dcCode={selectedDc} />
            </div>
          )}
          {step === 1 && (
            <NetBoxPreviewStep
              url={connUrl}
              token={connToken}
              sites={sites}
              onPreviewReady={handlePreviewReady}
            />
          )}
          {step === 2 && previewResult && (
            <NetBoxApplyStep
              url={connUrl}
              token={connToken}
              dcCode={selectedDc}
              siteId={selectedSiteId}
              conflictStrategy={conflictStrategy}
              selectedRackNbIds={selectedRackNbIds}
              previewSummary={previewResult.summary}
            />
          )}
        </div>
      </div>
        </>
      )}
    </div>
  )
}
